import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  getUserPreferences,
  updateUserPreferences,
  type UserPreferencesData,
  type UserPreferencesPatch,
  type UserPreferencesResponse,
} from "@/services/user-preferences"

const USER_PREFERENCES_QUERY_KEY = ["user-preferences"] as const
const NOTIFICATION_HISTORIES_QUERY_KEY = ["notification-histories"] as const
const inFlightPreferenceUpdates = new Map<
  string,
  Promise<{ status: boolean; data: UserPreferencesResponse }>
>()

function getPreferencePatchKey(patch: UserPreferencesPatch) {
  return JSON.stringify(patch)
}

type UserPreferencesQueryData =
  | {
      status?: boolean
      data?: UserPreferencesResponse
      preferences?: UserPreferencesData
    }
  | undefined

function applyPatchToPreferences(
  current: UserPreferencesData,
  patch: UserPreferencesPatch
): UserPreferencesData {
  return {
    ...current,
    ...(patch.language !== undefined ? { language: patch.language } : {}),
    ...(patch.theme !== undefined ? { theme: patch.theme } : {}),
    ...(patch.push_notification
      ? {
          push_notification: {
            ...(current.push_notification ?? { enabled: false }),
            ...patch.push_notification,
          },
        }
      : {}),
    ...(patch.email_notification
      ? {
          email_notification: {
            ...(current.email_notification ?? { enabled: false, email: null }),
            ...patch.email_notification,
          },
        }
      : {}),
  }
}

function applyPatchToQueryData(
  current: UserPreferencesQueryData,
  patch: UserPreferencesPatch
): UserPreferencesQueryData {
  if (!current) return current

  if (current.data?.preferences) {
    return {
      ...current,
      data: {
        ...current.data,
        preferences: applyPatchToPreferences(current.data.preferences, patch),
      },
    }
  }

  if (current.preferences) {
    return {
      ...current,
      preferences: applyPatchToPreferences(current.preferences, patch),
    }
  }

  return current
}

export function useUserPreferences(enabled = true) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: USER_PREFERENCES_QUERY_KEY,
    queryFn: () => getUserPreferences(),
    enabled,
    staleTime: 60_000,
  })

  const mutation = useMutation({
    mutationFn: (patch: UserPreferencesPatch) => {
      const key = getPreferencePatchKey(patch)
      const inFlight = inFlightPreferenceUpdates.get(key)
      if (inFlight) return inFlight

      const request = updateUserPreferences(patch).finally(() => {
        inFlightPreferenceUpdates.delete(key)
      })
      inFlightPreferenceUpdates.set(key, request)
      return request
    },
    onSuccess: (res, patch) => {
      if (res?.data) {
        queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, res)
      }

      if (patch.language) {
        queryClient.invalidateQueries({
          queryKey: NOTIFICATION_HISTORIES_QUERY_KEY,
        })
      }
    },
  })

  const updatePreferences = async (patch: UserPreferencesPatch) => {
    const previousData = queryClient.getQueryData<UserPreferencesQueryData>(
      USER_PREFERENCES_QUERY_KEY
    )

    queryClient.setQueryData<UserPreferencesQueryData>(
      USER_PREFERENCES_QUERY_KEY,
      (current) => applyPatchToQueryData(current, patch)
    )

    await queryClient.cancelQueries({ queryKey: USER_PREFERENCES_QUERY_KEY })

    try {
      return await mutation.mutateAsync(patch)
    } catch (error) {
      if (previousData !== undefined) {
        queryClient.setQueryData(USER_PREFERENCES_QUERY_KEY, previousData)
      }
      throw error
    }
  }

  const updatePreferencesOptimistic = updatePreferences

  const raw = query.data as
    | {
        data?: { preferences?: UserPreferencesData }
        preferences?: UserPreferencesData
      }
    | undefined
  const preferences: UserPreferencesData | undefined =
    raw?.data?.preferences ?? raw?.preferences

  return {
    preferences,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isUpdating: mutation.isPending,
    updatePreferences,
    updatePreferencesOptimistic,
    refetch: query.refetch,
  }
}
